import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../util/authContext';
import { useAlert } from '../../util/alertDialogContext';
import { needsMfa } from "../../util/guards";

const REDIRECT_REASONS = {
  session_expired: {
    title: 'Session Expired',
    msg: 'Your session has expired. Please sign in again.'
  },
  subscription_inactive: {
    title: 'Subscription Required',
    msg: 'Your subscription is not currently active. Please check your subscription settings.'
  },
  mfa_required: {
    title: 'MFA Setup Required',
    msg: 'Your organization requires multi-factor authentication. Please set up MFA in your account settings.'
  },
  account_expired: {
    title: 'Account Expired',
    msg: 'Your account has expired. Please renew your subscription to continue.'
  }
};

export function setRedirectReason(reason) {
  sessionStorage.setItem('redirectReason', reason);
}

function Page({ component: Component, guard, ...props }) {
  const user = useAuth() || {};
  const auth = {
    isAuthenticated: false,
    authDate: '',
    data: {},
    removeSession: () => {},
    ...user,
  };
  const location = useLocation();
  const params = useParams();
  const { alert } = useAlert();
  
  // Pass route params to component (e.g., id, token)
  const page = <Component {...props} {...params} />;

  //console.log("Attempting to render page...");

  // Show redirect reason alert if present
  React.useEffect(() => {
    const reason = sessionStorage.getItem('redirectReason');
    if (reason && REDIRECT_REASONS[reason]) {
      sessionStorage.removeItem('redirectReason');
      alert(REDIRECT_REASONS[reason]);
    }
  }, [location]);

  React.useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag("page_view", location.pathname + location.search);
    }
  }, [location]);

  /**
   * When no guard provided
   * or user is not authenticated and guard allows - rendering the page
   */
  if (!guard || (!auth.isAuthenticated && guard(false))) {
    //console.log("Page has no guard or user isn't authenticated and the guard is okay with that.");
    return page;
  }

  /**
   * When cookie has expired, reset local storage authentication flags
   */
  if (auth.authDate && new Date() >= auth.authDate) {
    //console.log("Removing session in page, due to authDate being old.");
    auth.removeSession();
  }

  /**
   * When user is not authenticated and guard denies - redirecting a user
   * to the page according to his user data
   */
  if (!auth.isAuthenticated && !guard(false)) {
    let redirect = redirectTo(false);
    //console.log(`Redirecting to '${redirect}' due to user not being authenticated...`);
    return <Navigate to={redirect} replace />;
  }

  /**
   * Otherwise rendering a component for Authenticated users
   */
  return (
    <Authenticated guard={guard} user={auth} currentPath={location.pathname}>
      {page}
    </Authenticated>
  );
}

const Authenticated = ({ children, guard, user, currentPath }) => {
  if (!guard(true, user)) {
    let goingTo = redirectTo(true, user?.data || {});

    // Prevent redirect loops (e.g. "/" -> "/").
    if (goingTo === currentPath) {
      if (currentPath !== '/account') {
        goingTo = '/account';
      } else {
        return children;
      }
    }

    // Set reason so the target page can explain the redirect
    const userData = user?.data || {};
    if (needsMfa(true, { data: userData })) {
      setRedirectReason('mfa_required');
    } else if (userData.subscriptionStatus && userData.subscriptionStatus !== 'active') {
      setRedirectReason('subscription_inactive');
    }

    return <Navigate to={goingTo} replace />;
  }

  //console.log("Rendering children...");

  return children;
};

export const redirectTo = (isAuthenticated, userData = {}) => {
  const safeUserData = userData || {};

  if (!isAuthenticated) {
    return '/signin';
  }

  if (!safeUserData.email) {
    return '/account';
  }

  if (needsMfa(true, {data: safeUserData})) {
    return '/account';
  }

  if (safeUserData.subscriptionStatus && safeUserData.subscriptionStatus !== "active") {
    console.warn("Subscription is not active!");
    return '/account';
  }

  return '/';
};

export default Page;
